import { useEffect, useRef, useCallback } from 'react';
import { createSSEConnection } from '@/utils/api';
import { useFeedStore, useUIStore } from '@/store';
import type { SSEEvent } from '@/types';

export function useSSE(enabled: boolean = true) {
  const eventSourceRef = useRef<EventSource | null>(null);
  const { prependTweets } = useFeedStore();
  const { addToast } = useUIStore();
  
  const connect = useCallback(() => {
    if (!enabled) {
      return;
    }
    
    if (eventSourceRef.current?.readyState === EventSource.OPEN) {
      return;
    }
    
    const eventSource = createSSEConnection();
    eventSourceRef.current = eventSource;
    
    eventSource.onopen = () => {
      console.log('SSE connection opened');
    };
    
    eventSource.onmessage = (event) => {
      try {
        const data: SSEEvent = JSON.parse(event.data);
        
        switch (data.type) {
          case 'new_tweets':
            if (data.tweets && data.tweets.length > 0) {
              prependTweets(data.tweets);
              addToast({
                title: '新推文',
                description: `收到 ${data.tweets.length} 条新推文`,
              });
            }
            break;
            
          case 'heartbeat':
            // Heartbeat received, connection is alive
            break;
            
          case 'error':
            console.error('SSE error:', data.message);
            break;
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };
    
    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      
      // Close and reconnect after delay
      eventSource.close();
      eventSourceRef.current = null;
      
      setTimeout(() => {
        connect();
      }, 5000);
    };
  }, [prependTweets, addToast]);
  
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);
  
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect, enabled]);
  
  return { connect, disconnect };
}
